import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { handleToast } from '../../Toast';
import { useActivesWallet } from '../../../jsx/pages/Wallet/hooks/ActivesHooks';
import { Container } from '../SimpleActive/styles';

const TRADES_URL = '/api/international/trades/';

const operationButtonStyle = (selected, selectedColor) => ({
  flex: 1,
  padding: '0.6rem 0',
  borderRadius: 8,
  border: 'none',
  cursor: 'pointer',
  fontWeight: 600,
  backgroundColor: selected ? selectedColor : '#ECF0F3',
  color: selected ? '#FFF' : '#303F4B',
});

const today = () => new Date().toISOString().slice(0, 10);
const apiUrl = process.env.REACT_APP_API_URL;

/**
 * Formulário de trade (compra/venda) de ativo internacional.
 *
 * - Sem `trade`: modo criação (POST em /international/trades/).
 * - Com `trade`: modo edição (PATCH em /international/trades/<id>/).
 * - `lockSide`: esconde o seletor BUY/SELL (ex.: editar o 1º BUY no ManualImport).
 */
export function InternationalTradeForm({
  ticker,
  trade = null,
  lockSide = false,
  onCancel,
  onSaved,
}) {
  const { updateWallet } = useActivesWallet();
  const isEdit = Boolean(trade?.id);
  const symbol = String(ticker || trade?.ticker || '').toUpperCase();

  const [side, setSide] = useState(trade?.side || 'BUY');
  const [amount, setAmount] = useState(trade ? String(trade.quantity ?? '') : '');
  const [tradeDate, setTradeDate] = useState(trade?.trade_date || today());
  const [priceUsd, setPriceUsd] = useState(
    trade?.price_usd != null ? String(trade.price_usd) : ''
  );
  const [priceTotal, setPriceTotal] = useState(
    trade?.net_amount_usd != null ? String(trade.net_amount_usd) : ''
  );
  const [brokerOrigin, setBrokerOrigin] = useState(trade?.broker_origin || '');
  const [loading, setLoading] = useState(false);

  const amountRef = useRef(amount);
  amountRef.current = amount;
  // Em edição não auto-buscamos o preço ao montar (preservamos o já registrado).
  const skipAutoPrice = useRef(isEdit);

  function handleAmountChange(value) {
    setAmount(value);
    if (Number(value) > 0 && Number(priceUsd) > 0) {
      setPriceTotal((Number(value) * Number(priceUsd)).toFixed(2));
    }
  }

  function handleUnitPriceChange(value) {
    setPriceUsd(value);
    if (Number(amount) > 0 && Number(value) > 0) {
      setPriceTotal((Number(amount) * Number(value)).toFixed(2));
    }
  }

  function handleTotalChange(value) {
    setPriceTotal(value);
    if (Number(amount) > 0 && Number(value) > 0) {
      setPriceUsd((Number(value) / Number(amount)).toFixed(4));
    }
  }

  // Busca o valor unitário (close) na data escolhida — exceto na 1ª render em edição.
  useEffect(() => {
    if (!symbol || !tradeDate) return;
    if (skipAutoPrice.current) {
      skipAutoPrice.current = false;
      return;
    }
    let isCurrent = true;
    (async () => {
      try {
        const response = await axios.get(
          `${apiUrl}/api/international/assets/${symbol}/price`,
          { params: { date: tradeDate } }
        );
        if (isCurrent && response.data?.value != null) {
          const unit = Number(response.data.value);
          setPriceUsd(String(unit));
          const quantity = Number(amountRef.current);
          if (quantity > 0) setPriceTotal((quantity * unit).toFixed(2));
        }
      } catch (error) {
        if (isCurrent) {
          handleToast(true, 'Não foi possível obter o valor unitário na data');
        }
      }
    })();
    return () => {
      isCurrent = false;
    };
  }, [symbol, tradeDate]);

  function validate() {
    if (!amount || Number(amount) <= 0) {
      return handleToast(true, 'Informe a quantidade');
    }
    if (!tradeDate) return handleToast(true, 'A data da operação é obrigatória');
    if (!priceUsd || Number(priceUsd) <= 0) {
      return handleToast(true, 'O preço unitário (USD) é obrigatório');
    }
    if (!brokerOrigin) {
      return handleToast(true, 'A corretora de origem é obrigatória');
    }
  }

  async function submit() {
    if (validate()) return;
    setLoading(true);

    const payload = {
      asset: symbol,
      trade_date: tradeDate,
      side,
      quantity: String(amount),
      price_usd: String(priceUsd),
      broker_origin: String(brokerOrigin).toUpperCase(),
    };
    if (priceTotal) payload.price_total = priceTotal;

    try {
      if (isEdit) {
        await axios.patch(`${apiUrl}${TRADES_URL}${trade.id}/`, payload);
        handleToast(false, 'Operação atualizada com sucesso');
      } else {
        await axios.post(`${apiUrl}${TRADES_URL}`, payload);
        handleToast(
          false,
          side === 'BUY' ? 'Compra registrada com sucesso' : 'Venda registrada com sucesso'
        );
      }
      updateWallet();
      onSaved?.();
    } catch (err) {
      const data = err?.response?.data;
      const message =
        (Array.isArray(data) && data[0]) ||
        data?.detail ||
        data?.message ||
        (typeof data === 'string' ? data : '') ||
        'Erro ao salvar a operação';
      handleToast(true, message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Container>
      <div className="form-group mb-3">
        <label className="mb-1">
          <strong>Ativo</strong>
        </label>
        <input type="text" className="form-control" value={symbol} disabled />
      </div>

      {!lockSide && (
        <div className="form-group mb-3">
          <label className="mb-1">
            <strong>Operação</strong>
          </label>
          <div className="row-content">
            <button
              type="button"
              style={operationButtonStyle(side === 'BUY', '#1FA75B')}
              onClick={() => setSide('BUY')}
            >
              Comprar +
            </button>
            <button
              type="button"
              style={operationButtonStyle(side === 'SELL', '#EC6643')}
              onClick={() => setSide('SELL')}
            >
              Vender -
            </button>
          </div>
        </div>
      )}

      <div className="form-group mb-3">
        <label className="mb-1">
          <strong>Data da operação</strong>
        </label>
        <input
          type="date"
          className="form-control"
          value={tradeDate}
          onChange={(e) => setTradeDate(e.target.value)}
        />
      </div>

      <div className="form-group mb-3">
        <label className="mb-1">
          <strong>Quantidade</strong>
        </label>
        <input
          type="number"
          step="0.01"
          className="form-control"
          placeholder="ex: 10"
          value={amount}
          onChange={(e) => handleAmountChange(e.target.value)}
        />
      </div>

      <div className="form-group mb-3">
        <label className="mb-1">
          <strong>Preço unitário (USD)</strong>
        </label>
        <input
          type="number"
          step="0.0001"
          className="form-control"
          placeholder="ex: 150.50"
          value={priceUsd}
          onChange={(e) => handleUnitPriceChange(e.target.value)}
        />
      </div>

      <div className="form-group mb-3">
        <label className="mb-1">
          <strong>Valor total (USD)</strong>
        </label>
        <input
          type="number"
          step="0.01"
          className="form-control"
          placeholder="auto: quantidade × preço"
          value={priceTotal}
          onChange={(e) => handleTotalChange(e.target.value)}
        />
      </div>

      <div className="form-group mb-3">
        <label className="mb-1">
          <strong>Corretora de origem</strong>
        </label>
        <input
          type="text"
          className="form-control"
          placeholder="ex: AVENUE"
          value={brokerOrigin}
          onChange={(e) => setBrokerOrigin(e.target.value)}
        />
      </div>

      <div className="text-center mt-4 row-content">
        {onCancel && (
          <button
            type="button"
            className="btn btn-secondary btn-block"
            disabled={loading}
            onClick={onCancel}
          >
            Cancelar
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary btn-block"
          disabled={loading}
          onClick={submit}
        >
          {isEdit
            ? 'Salvar alterações'
            : side === 'BUY'
            ? 'Registrar compra'
            : 'Registrar venda'}
        </button>
      </div>
    </Container>
  );
}
